import { memo, useCallback, useEffect, useMemo } from 'react';
import BrandKitFontPicker from './BrandKitFontPicker';
import { getFontEntriesFromPlatformBrandKit, loadStylesheetFont, resolveFontDisplayName } from './fontKitUtils';
import Icon from '@/components/atoms/Icon';
import { useClipsContext } from '@/context/ClipsContext/ClipsContext';
import usePlatformBrandKits from '@/hooks/usePlatformBrandKits';
import { loadFont } from '@/libs/fonts';
import { classnames } from '@/libs/utils';
import type { FontSelectorProps } from '@/Pages/Clip/SideBar/FontSelector/FontSelector';
import { FONT_DEFAULT_VALUE, FONTS_DEFAULT } from '@/player-config/src/constants/fonts';
import { SelectionElementType } from '@/player-config/src/types/RemotionPlayerConfigTypes/CompositionConfig';
import { getFontLocationFromAssetMetadata, isFontStylesheetUrl } from '@/player-config/src/utils/fontUtils';
import { updateCaptionFontLocation, updateSpeakerLabelStyles } from '@/stores/clip';

function PlatformBrandFontSelector({
  isInline = false,
  fontTarget = SelectionElementType.CAPTIONS,
  onChange,
  selectedFontName,
  selectedFontLocation,
  useDynamicPosition = false,
  onOpen,
  inline,
  onBack
}: FontSelectorProps) {
  const { clipId, clipData } = useClipsContext();
  const { brandKits } = usePlatformBrandKits();

  const fontLocation = useMemo(() => {
    return selectedFontLocation || getFontLocationFromAssetMetadata(clipData.asset_metadata, fontTarget);
  }, [clipData.asset_metadata, fontTarget, selectedFontLocation]);

  /**
   * Collect entries from ALL available brand kits so we can resolve a display name
   * even when the clip was saved while a different brand kit was active.
   */
  const allPlatformEntries = useMemo(() => {
    return brandKits.flatMap(kit => getFontEntriesFromPlatformBrandKit(kit));
  }, [brandKits]);

  const displayedFontName = useMemo(() => {
    if (selectedFontName) {
      return selectedFontName;
    }
    return resolveFontDisplayName(fontLocation, allPlatformEntries, FONTS_DEFAULT, FONT_DEFAULT_VALUE.name);
  }, [selectedFontName, fontLocation, allPlatformEntries]);

  // Load the font binary so the trigger-button preview renders with the correct typeface.
  useEffect(() => {
    if (fontLocation && displayedFontName && !isFontStylesheetUrl(fontLocation)) {
      loadFont(fontLocation, displayedFontName);
    }
  }, [fontLocation, displayedFontName]);

  // Inject a <link rel="stylesheet"> for web-font CSS imports so the trigger-button preview works.
  useEffect(() => {
    if (!fontLocation || !isFontStylesheetUrl(fontLocation)) {
      return;
    }
    loadStylesheetFont(fontLocation);
  }, [fontLocation]);

  const onPlatformFontSelect = useCallback(
    (url: string, fontName: string) => {
      if (onChange) {
        onChange(fontName, url);
      } else if (fontTarget === SelectionElementType.SPEAKER_LABEL) {
        updateSpeakerLabelStyles(clipId, { font_location: url });
      } else {
        updateCaptionFontLocation(clipId, url);
      }
    },
    [clipId, fontTarget, onChange]
  );

  // Always quote so names containing %, (, ) or other non-identifier characters
  // (e.g. URL-encoded filenames like acuminpro-medium%20%281%29) are valid CSS.
  const fontFamilyForPreview = useMemo(() => {
    return displayedFontName ? `"${displayedFontName}"` : displayedFontName;
  }, [displayedFontName]);

  return (
    <BrandKitFontPicker
      selectedFontUrl={fontLocation ?? ''}
      selectedFontDisplayName={displayedFontName}
      onFontSelect={onPlatformFontSelect}
      title="Font Input"
      useDynamicPosition={useDynamicPosition}
      classNames={classnames(
        'w-full min-w-0 truncate rounded-lg text-sm font-bold',
        isInline ? 'group/button h-full border p-2 hover:bg-slate-100' : 'border p-3'
      )}
      onOpen={onOpen}
      inline={inline}
      onBack={onBack}
    >
      <div className="flex w-full min-w-0 items-center justify-between gap-1">
        <div
          className="grow truncate text-left"
          style={{ fontFamily: fontFamilyForPreview }}
          data-testid="font-selector-button"
        >
          {displayedFontName}
        </div>
        <Icon name="IconChevronDown" size={16} className="shrink-0" />
      </div>
    </BrandKitFontPicker>
  );
}

export default memo(PlatformBrandFontSelector);
